<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { checkImportUsersPermission } from '@ilteducation/customer-mgmt-permissions';

import { useLocale } from '../composables/useLocale';
import { useSideBar } from '../composables/useSideBar';
import PermissionLock from './PermissionLock.vue';
import { useEscalateLogin } from '../composables/useEscalateLogin';
import { usePermissions } from '../composables/usePermissions';
import useMainTableFilters from './table/filters/useMainTableFilters';
import { useCurrentOrganization } from '../composables/useCurrentOrganization';
import { useSelectedItems } from '../composables/useSelectedItems';

import Search from './Search/index.vue';
import UserGroupSwitch, { SwitchValue } from '../containers/UserGroupSwitch.vue';
import MyAccountOrganizationsSelect from '../containers/MyAccountOrganizationsSelect.vue';

const { t } = useLocale();

const { sideBarOpen, sideBarWidth } = useSideBar();
const { mdAndUp, xlAndUp, lgAndUp, xs, width, thresholds } = useDisplay();

const isMobile = computed(() => {
  if (!sideBarOpen.value || xs.value) {
    return xs.value;
  }
  if (width.value - sideBarWidth.value < thresholds.value.sm) {
    return true;
  }
  return xs.value;
});

const hideToggleText = computed(() => {
  if (xlAndUp.value) {
    return false;
  }

  if (sideBarOpen.value) {
    return mdAndUp.value;
  }

  return false;
});

type Props = {
  list: SwitchValue;
  selectedOrganization?: string;
  searchString?: string;
  createAction: () => void;
  importUsers: () => void;
};

const props = withDefaults(defineProps<Props>(), {
  list: 'users' as const,
});

const { organization } = useCurrentOrganization();
const { showFiltersButton, activeFiltersCount, openFiltersModal } = useMainTableFilters();
const { permissionContext, reporterWithReason, getCallerAssociation } = usePermissions();
const { requestEscalatedLogin } = useEscalateLogin();
const { clearSelectedItems } = useSelectedItems();

const theSearchString = ref(props.searchString);
watch(
  () => props.searchString,
  (value) => {
    theSearchString.value = value;
  },
);

const theSelectedOrganization = ref(props.selectedOrganization);
watch(
  () => props.selectedOrganization,
  (value) => {
    theSelectedOrganization.value = value;
  },
);

const emits = defineEmits<{
  (event: 'setSelectedList', value: string): void;
  (event: 'setSelectedOrganization', value?: string): void;
  (event: 'setSearchString', value?: string): void;
}>();

const setSearchString = (value?: string) => {
  theSearchString.value = value;
  emits('setSearchString', value);
};

const notFoundAlert = ref<boolean>(false);
const onOrganizationNotFound = () => {
  notFoundAlert.value = true;
};

const create = () => {
  props.createAction();
};

const importUsersEnabledCheck = ref<ReturnType<typeof reporterWithReason>>({
  allowed: false,
  reason: 'not_permitted',
});
watch(
  [organization],
  async ([newOrganization]) => {
    if (!newOrganization) {
      importUsersEnabledCheck.value = { allowed: false, reason: 'not_permitted' };
      return;
    }

    importUsersEnabledCheck.value = await checkImportUsersPermission(
      permissionContext.value,
      { organization: { uid: newOrganization.uid } },
      getCallerAssociation,
      reporterWithReason,
    );
  },
  { immediate: true },
);

const onClickImportUsers = () => {
  if (importUsersEnabledCheck.value.allowed) {
    props.importUsers();
  } else if (importUsersEnabledCheck.value.reason === 'mfa_required') {
    requestEscalatedLogin();
  }
};

const onFilterButtonClick = () => {
  clearSelectedItems();
  openFiltersModal();
};
</script>

<template>
  <v-row>
    <v-col class="px-3" cols="12" :lg="hideToggleText ? 'auto' : true" order="0" order-md="0" order-lg="1">
      <div class="d-flex justify-center">
        <MyAccountOrganizationsSelect
          v-model="theSelectedOrganization"
          class="flex-0-0-100 select-size"
          :small-mode="hideToggleText && lgAndUp"
          :label="t('lcp.select_organization')"
          rounded="lg"
          @update:model-value="(value: string | undefined) => emits('setSelectedOrganization', value)"
          @not-found="onOrganizationNotFound"
        />
      </div>
    </v-col>
    <v-col class="px-3" cols="12" sm="12" md="auto" lg="auto" order="1" order-md="1" order-lg="0">
      <UserGroupSwitch
        :model-value="list"
        :hideText="hideToggleText"
        @change="(value: SwitchValue) => emits('setSelectedList', value)"
      />
    </v-col>
    <v-col class="px-3" :cols="false" :md="true" :lg="true" order="2">
      <Search v-model="theSearchString" :list-type="list" @on-search-change="setSearchString(theSearchString)"></Search>
    </v-col>
    <v-col v-if="showFiltersButton" class="px-3" cols="auto" md="auto" lg="auto" order="3">
      <v-badge location="top right" color="info" :content="activeFiltersCount" :model-value="activeFiltersCount > 0">
        <v-btn
          v-if="isMobile"
          icon="mdi-filter-variant"
          :aria-label="t('lcp.filters')"
          @click="onFilterButtonClick"
          rounded="lg"
          color="primary"
        />
        <v-btn v-else prepend-icon="mdi-filter-variant" @click="onFilterButtonClick" rounded="lg" color="primary">
          {{ t('lcp.filters') }}
        </v-btn>
      </v-badge>
    </v-col>
    <v-col class="px-3" cols="auto" md="auto" lg="auto" order="3">
      <template v-if="list === 'groups'">
        <v-btn v-if="isMobile" icon="mdi-plus" :aria-label="t('lcp.new_group')" v-bind="props" @click="create"></v-btn>
        <v-btn v-else prepend-icon="mdi-plus" rounded="lg" v-bind="props" @click="create">
          {{ t(`lcp.new_group`) }}
        </v-btn>
      </template>
      <template v-else>
        <v-btn-group divided color="primary" rounded="lg">
          <v-btn v-if="isMobile" icon="mdi-plus" :aria-label="t('lcp.new_user')" v-bind="props" @click="create"></v-btn>
          <v-btn v-else prepend-icon="mdi-plus" v-bind="props" @click="create">
            {{ t(`lcp.new_user`) }}
          </v-btn>

          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn icon="mdi-chevron-down" :aria-label="t('lcp.select_action')" v-bind="props"></v-btn>
            </template>

            <v-list>
              <v-list-item @click="create"> {{ t(`lcp.new_user`) }}</v-list-item>
              <v-list-item @click="onClickImportUsers" class="cursor-pointer">
                <PermissionLock v-if="!importUsersEnabledCheck.allowed" :reason="importUsersEnabledCheck.reason" />
                {{ t(`lcp.import_users_button`) }}
              </v-list-item>
            </v-list>
          </v-menu>
        </v-btn-group>
      </template>
    </v-col>
  </v-row>

  <v-row v-if="notFoundAlert">
    <v-col>
      <v-alert v-model="notFoundAlert" border="start" variant="tonal" type="info" closable>
        {{ t('lcp.organization_not_found_alert') }}
      </v-alert>
    </v-col>
  </v-row>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/lib/styles/settings/variables' as *;

:deep(.select-size) {
  @media #{map.get($display-breakpoints, 'md-and-down')} {
    width: 190px;
    flex: none !important;
  }
}
</style>
